import React, { useEffect, useState } from 'react';
import { adminAPI } from '../config/apiService';
import { ACCESS_LABELS } from './access';

// Settings -> Rights Management.
// Grid of admins with one checkbox per SIPAdminAccess flag; the row's mask
// is the sum of the checked bits. Save writes the mask (0 revokes admin
// entirely). Reset code generates a fresh 10-character admin code and shows
// it ONCE — it's stored only as a salted hash.
function RightsManager() {
    const [rows, setRows] = useState([]);
    const [edits, setEdits] = useState({});       // employeeID -> mask
    const [newEmpId, setNewEmpId] = useState('');
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState({});         // employeeID -> 'saving'|'code'
    const [freshCode, setFreshCode] = useState(null); // { employeeID, name, code }
    const [msg, setMsg] = useState('');
    const [msgKind, setMsgKind] = useState('info');

    const flash = (text, kind = 'info') => { setMsg(text); setMsgKind(kind); };
    const myEmployeeId = Number(localStorage.getItem('admin_employee_id') || 0);

    const reload = async () => {
        setLoading(true);
        try {
            const res = await adminAPI.getAdminRights();
            const list = Array.isArray(res.data) ? res.data : [];
            setRows(list);
            const seed = {};
            for (const r of list) seed[r.employeeID] = r.sipAdminAccessID ?? 0;
            setEdits(seed);
        } catch (err) {
            flash(`Load failed: ${err?.response?.data || err?.message || 'unknown error'}`, 'error');
        } finally { setLoading(false); }
    };

    useEffect(() => { reload(); }, []);

    const toggleBit = (employeeID, bit) => {
        setEdits((prev) => ({ ...prev, [employeeID]: (prev[employeeID] ?? 0) ^ bit }));
    };

    const isDirty = (employeeID) => {
        const row = rows.find((r) => r.employeeID === employeeID);
        return row && (edits[employeeID] ?? 0) !== (row.sipAdminAccessID ?? 0);
    };

    const saveRow = async (employeeID) => {
        const mask = edits[employeeID] ?? 0;
        if (mask === 0 && employeeID === myEmployeeId) {
            flash("You can't remove all of your own access — another admin must do that.", 'error');
            return;
        }
        if (mask === 0 && !window.confirm('Mask of 0 fully revokes admin access for this person. Continue?')) return;
        setBusy((p) => ({ ...p, [employeeID]: 'saving' })); setMsg('');
        try {
            await adminAPI.setAdminRights(employeeID, mask);
            await reload();
            flash('Rights saved.', 'success');
        } catch (err) {
            flash(`Save failed: ${err?.response?.data || err?.message || 'unknown error'}`, 'error');
        } finally {
            setBusy((p) => { const { [employeeID]: _, ...rest } = p; return rest; });
        }
    };

    const resetCode = async (employeeID, name) => {
        if (!window.confirm(`Generate a new admin code for ${name}? Their current code stops working immediately.`)) return;
        setBusy((p) => ({ ...p, [employeeID]: 'code' })); setMsg('');
        try {
            const res = await adminAPI.resetAdminCode(employeeID);
            setFreshCode({ employeeID, name, code: res.data?.code || '' });
            await reload();
        } catch (err) {
            flash(`Code reset failed: ${err?.response?.data || err?.message || 'unknown error'}`, 'error');
        } finally {
            setBusy((p) => { const { [employeeID]: _, ...rest } = p; return rest; });
        }
    };

    const addAdmin = async () => {
        const id = parseInt(newEmpId, 10);
        if (!id) { flash('Enter a valid Employee ID.', 'error'); return; }
        if (rows.some((r) => r.employeeID === id)) { flash('That employee is already in the list.', 'info'); return; }
        setBusy((p) => ({ ...p, add: true })); setMsg('');
        try {
            // Makes them visible with no pages; check boxes + Save to grant.
            await adminAPI.addAdmin(id);
            setNewEmpId('');
            await reload();
            flash('Employee added — check their pages and Save, then Reset code.', 'success');
        } catch (err) {
            flash(`Add failed: ${err?.response?.data || err?.message || 'unknown error'}`, 'error');
        } finally {
            setBusy((p) => { const { add: _, ...rest } = p; return rest; });
        }
    };

    const removeAdmin = async (employeeID, name) => {
        if (employeeID === myEmployeeId) { flash("You can't remove your own admin access.", 'error'); return; }
        if (!window.confirm(`Remove ${name} from admin entirely? Their access and sign-in code stop working.`)) return;
        setBusy((p) => ({ ...p, [employeeID]: 'saving' })); setMsg('');
        try {
            await adminAPI.removeAdmin(employeeID);
            await reload();
            flash('Admin removed.', 'success');
        } catch (err) {
            flash(`Remove failed: ${err?.response?.data || err?.message || 'unknown error'}`, 'error');
        } finally {
            setBusy((p) => { const { [employeeID]: _, ...rest } = p; return rest; });
        }
    };

    if (loading) return <div style={styles.muted}>Loading…</div>;

    return (
        <div>
            <div style={styles.card}>
                <div style={styles.eyebrow}>Rights Management</div>
                <div style={styles.title}>Admin access</div>
                <p style={styles.muted}>
                    Check the pages each admin can use; Save writes the combined access value.
                    Reset code issues a fresh 10-character sign-in code (shown once).
                </p>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                    <input
                        type="number" placeholder="Employee ID"
                        value={newEmpId} onChange={(e) => setNewEmpId(e.target.value)}
                        style={{ ...styles.input, width: 130 }}
                    />
                    <button onClick={addAdmin} disabled={!!busy.add || !newEmpId} style={btn('#15803d', !!busy.add || !newEmpId)}>
                        + Add admin
                    </button>
                </div>
            </div>

            {freshCode && (
                <div style={styles.codeCard}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: '#92400e' }}>
                        New code for {freshCode.name}
                    </div>
                    <div style={styles.codeBox}>{freshCode.code}</div>
                    <div style={{ fontSize: 12, color: '#92400e' }}>
                        Copy it now — it is not stored and can't be shown again.
                    </div>
                    <button onClick={() => setFreshCode(null)} style={{ ...btn('#92400e', false), marginTop: 8 }}>Dismiss</button>
                </div>
            )}

            {msg && <Banner kind={msgKind}>{msg}</Banner>}

            <div style={{ ...styles.card, marginTop: 12 }}>
                <table style={styles.table}>
                    {/* Fixed column widths so the grid always fits the card
                        (no horizontal scroll) and the checkbox headers wrap.
                        Admin column has no width -> it absorbs the slack when
                        the browser is wide, keeping the rest anchored. */}
                    <colgroup>
                        <col />
                        {ACCESS_LABELS.map((a) => <col key={a.bit} style={{ width: 72 }} />)}
                        <col style={{ width: 118 }} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th style={styles.th}>Admin</th>
                            {ACCESS_LABELS.map((a) => (
                                <th key={a.bit} style={styles.thCheck} title={a.label}>
                                    {a.label.replace('Qual Maint — ', 'QM ')}
                                </th>
                            ))}
                            <th style={styles.th}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const mask = edits[r.employeeID] ?? 0;
                            const rowBusy = busy[r.employeeID];
                            return (
                                <tr key={r.employeeID} style={{ background: isDirty(r.employeeID) ? '#fefce8' : 'transparent' }}>
                                    <td style={styles.td}>
                                        <div style={{ fontWeight: 700 }}>{r.firstName} {r.lastName}</div>
                                        <div style={{ fontSize: 11, color: '#6b7280' }}>
                                            #{r.employeeID}{r.employeeID === myEmployeeId && ' (you)'} · mask {mask}
                                        </div>
                                        {r.employeeID !== myEmployeeId && (
                                            <button
                                                onClick={() => removeAdmin(r.employeeID, `${r.firstName} ${r.lastName}`)}
                                                disabled={!!rowBusy}
                                                style={styles.removeLink}
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </td>
                                    {ACCESS_LABELS.map((a) => (
                                        <td key={a.bit} style={{ ...styles.td, textAlign: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={(mask & a.bit) !== 0}
                                                onChange={() => toggleBit(r.employeeID, a.bit)}
                                            />
                                        </td>
                                    ))}
                                    <td style={{ ...styles.td }}>
                                        <div style={{ display: 'flex', gap: 4 }}>
                                            <button
                                                onClick={() => saveRow(r.employeeID)}
                                                disabled={!!rowBusy || !isDirty(r.employeeID)}
                                                style={btn('#2563eb', !!rowBusy || !isDirty(r.employeeID))}
                                            >
                                                {rowBusy === 'saving' ? '…' : 'Save'}
                                            </button>
                                            <button
                                                onClick={() => resetCode(r.employeeID, `${r.firstName} ${r.lastName}`)}
                                                disabled={!!rowBusy}
                                                style={btn('#6b7280', !!rowBusy)}
                                                title="Issue a fresh 10-character sign-in code"
                                            >
                                                {rowBusy === 'code' ? '…' : 'Reset'}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Banner({ kind, children }) {
    const color = kind === 'error' ? '#dc2626' : kind === 'success' ? '#15803d' : '#374151';
    const bg = kind === 'error' ? '#fef2f2' : kind === 'success' ? '#f0fdf4' : '#f3f4f6';
    return <div style={{ margin: '12px 0 0', padding: '8px 12px', borderRadius: 4, background: bg, color, fontSize: 13 }}>{children}</div>;
}

const btn = (bg, disabled) => ({
    background: bg, color: 'white', border: 'none', padding: '3px 6px', borderRadius: 4,
    fontWeight: 600, fontSize: 10, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
    whiteSpace: 'nowrap'
});

const styles = {
    card: { background: 'white', padding: 16, borderRadius: 8, border: '1px solid #e5e7eb' },
    muted: { color: '#6b7280', fontSize: 13, lineHeight: 1.5 },
    eyebrow: { fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.1em', textTransform: 'uppercase' },
    title: { fontSize: 18, fontWeight: 800, color: '#111827', marginTop: 2, marginBottom: 6 },
    input: { padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 },
    // Fixed layout + 100% width: the table always fits the card exactly, so
    // there's no horizontal scroll however wide the browser gets, and the
    // colgroup widths force the checkbox headers to wrap.
    table: { width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' },
    th: { textAlign: 'left', padding: '5px 6px', fontSize: 9, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.03em', borderBottom: '1px solid #e5e7eb', verticalAlign: 'bottom' },
    // Narrow, wrapping headers for the checkbox columns so two-word labels
    // (e.g. "Monthly Points") stack instead of forcing the column wide.
    // whiteSpace normal + normal word-break: labels wrap only at spaces
    // ("QM SUPERVISORS" -> two lines) and never split a single word.
    thCheck: { textAlign: 'center', padding: '5px 3px', fontSize: 9, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.02em', borderBottom: '1px solid #e5e7eb', verticalAlign: 'bottom', whiteSpace: 'normal', lineHeight: 1.2, wordBreak: 'normal', overflowWrap: 'normal' },
    td: { padding: '5px 7px', fontSize: 13, color: '#111827', borderBottom: '1px solid #f3f4f6' },
    removeLink: { marginTop: 3, background: 'none', border: 'none', color: '#dc2626', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: 0, textDecoration: 'underline' },
    codeCard: { marginTop: 12, background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 8, padding: 16 },
    codeBox: { fontFamily: 'monospace', fontSize: 24, fontWeight: 800, letterSpacing: 4, background: 'white', border: '1px dashed #f59e0b', borderRadius: 6, padding: '10px 14px', margin: '8px 0', display: 'inline-block' }
};

export default RightsManager;
